import type { Booking, SortConfig } from '../../lib/types'
import { SectionLabel, EmptyState, Card, DataTable, DataTableHeader, DataTableBody } from '@perch/ui'
import { SortableHeader } from './SortableHeader'
import { PriceDropTableRow } from './PriceDropTableRow'
import type { Session } from '@supabase/supabase-js'

interface PriceDropsSectionProps {
  priceDrops: Booking[]
  total: number
  sortConfig: SortConfig
  onSort: (column: string) => void
  onBookingClick: (booking: Booking) => void
  onRebooked: (bookingId: string) => void
  onRefresh: () => void
  session: Session
}

export function PriceDropsSection({
  priceDrops,
  total,
  sortConfig,
  onSort,
  onBookingClick,
  onRebooked,
  onRefresh,
  session,
}: PriceDropsSectionProps) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <SectionLabel variant="emerald">Price Drops</SectionLabel>
          <h2 className="text-2xl font-serif font-normal text-stone-900 dark:text-stone-100 mt-1">
            {total} Flight{total !== 1 ? 's' : ''} Ready to Rebook
          </h2>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {priceDrops.length === 0 ? (
        <Card noPadding>
          <EmptyState
            icon={<svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" /></svg>}
            title="All caught up!"
            description="No price drops to rebook right now."
            iconColor="emerald"
          />
        </Card>
      ) : (
        <DataTable borderColor="emerald">
            <DataTableHeader color="emerald">
              <SortableHeader column="user" label="User" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="airline" label="Airline" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="route" label="Route" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="pnr" label="PNR" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="paid" label="Paid" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="now" label="Now" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader column="savings" label="Savings" sortConfig={sortConfig} onSort={onSort} />
              <th>Linear</th>
              <SortableHeader column="checked" label="Checked" sortConfig={sortConfig} onSort={onSort} />
              <th className="w-0"></th>
            </DataTableHeader>
            <DataTableBody emptyMessage="No price drops to rebook right now." colSpan={10}>
              {priceDrops.map(booking => (
                <PriceDropTableRow
                  key={booking.id}
                  booking={booking}
                  onClick={() => onBookingClick(booking)}
                  onRebooked={onRebooked}
                  session={session}
                />
              ))}
            </DataTableBody>
          </DataTable>
      )}
    </section>
  )
}
